/*****************************************************************
 *  CUSTOMER BUTLER - ZOHO'S RELATED-LIST BLOCKS (record page)     *
 *  File: apps/web/src/pages/customer/ZohoRelatedBlocks.js         *
 *  What it does: every related list on the record page, drawn the *
 *  way Zoho draws it - one block per list, in the rail's order:   *
 *  the title (with its count) on the left, its buttons on the     *
 *  right, then a TABLE when the list has columns, three columns   *
 *  for Open / Closed Activities, a muted "No records found" when  *
 *  empty (the block never hides), and "Not connected yet" for the *
 *  lists Butler has not joined up.                                *
 *    RelatedBlock            the block shell                       *
 *    NotConnectedBlock       a Zoho list Butler does not hold yet  *
 *    NotesBlock              office notes, add box on top          *
 *    PropertiesBlock         a customer's properties (table)       *
 *    ReportsBlock            past reports (table)                  *
 *    OpenActivitiesBlock / ClosedActivitiesBlock  three columns    *
 *    ContactsBlock / PeopleBlock / ConsentBlock   tables           *
 *    OtherZohoListsBlock     the rest of Zoho's lists, named once  *
 *    HistoryTimeline         Zoho's Timeline "History" by day      *
 *  Reads/Writes: only through the doors the tabs already used,    *
 *  with the same sheets (RelatedLists.js) - one copy of each.     *
 *****************************************************************/

/* WHY (2026-10-15, James via Fable, three Zoho screenshots): "the left rail
   is a jump list to blocks on one long page, not tabs." Every block: title
   left, actions right; a table for tabular lists; "No records found" when
   empty; Open / Closed Activities as Tasks / Meetings / Calls columns. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE BLOCK SHELL, THE EMPTY LINE, THE TABLE         *
 *  SECTION 2 - NOTES                                              *
 *  SECTION 3 - PROPERTIES AND REPORTS                             *
 *  SECTION 4 - OPEN AND CLOSED ACTIVITIES                         *
 *  SECTION 5 - CONTACTS, PEOPLE, CONSENT                          *
 *  SECTION 6 - THE OTHER ZOHO LISTS, AND THE TIMELINE HISTORY     *
 *****************************************************************/

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { StatusChip } from './customerParts';
import { formatMoment, formatDay } from './customerDates';
import { useCustomerAccessView } from './customerAccessView';
import { NotesTab } from './RecordTabs';
import {
  SmallFormSheet, NEW_PROPERTY_BOXES, NEW_CONTACT_BOXES, CONSENT_BOXES, CONSENT_WORDS,
  ACTIVITY_BOXES, CHANNEL_WORDS, streetWords,
} from './RelatedLists';
import { addProperty, addContact, recordConsent, logActivity } from './customerApi';
// WHY (2026-10-15, BACKLOG 317): a send pulled back shows its own chip.
import { lookForEvent } from './suiteEventLooks';

/*****************************************************************
 *  SECTION 1 - THE BLOCK SHELL, THE EMPTY LINE, THE TABLE         *
 *****************************************************************/

const NO_RECORDS_WORDS = 'No records found';                       // Zoho's own empty words
const NOT_CONNECTED_WORDS = 'Not connected yet. Zoho keeps these for now.';

export const OTHER_LISTS_BLOCK_ID = 'cb-rel-other-zoho-lists'; // the one shared muted block

/* ---- sharesTheOtherBlock(railEntry) -- a not-connected list with no block of its own ---- */
export function sharesTheOtherBlock(railEntry) {
  return !railEntry.block && !railEntry.ownBlock;
}

/* ---- blockIdFor(railEntry) -- the page anchor the rail jumps to ---- */
export function blockIdFor(railEntry) {
  if (sharesTheOtherBlock(railEntry)) {
    return OTHER_LISTS_BLOCK_ID;
  }

  const anchorWords = String(railEntry.label || 'list').toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return `cb-rel-${anchorWords}`;
}

/* ---- RelatedBlock({ blockId, title, count, actions, isMuted, children }) ----
   Zoho's block: title + count left, buttons right, body below.  */
export function RelatedBlock({ blockId, title, count, actions, isMuted, children }) {
  const blockClass = isMuted ? 'cb-zr-block is-muted' : 'cb-zr-block';
  const hasCount = typeof count === 'number';

  return (
    <section id={blockId} className={blockClass} aria-label={title}>
      <header className="cb-zr-block-head">
        <h2 className="cb-zr-block-title">
          {title}
          {hasCount ? <span className="cb-zr-count">{count}</span> : null}
        </h2>
        {actions ? <div className="cb-zr-block-acts">{actions}</div> : null}
      </header>
      <div className="cb-zr-block-body">{children}</div>
    </section>
  );
}

/* ---- EmptyLine({ words }) -- the muted one-liner of an empty list ---- */
export function EmptyLine({ words }) {
  return <p className="cb-zr-empty">{words || NO_RECORDS_WORDS}</p>;
}

/* ---- NotConnectedBlock({ blockId, title }) -- honest, muted, never hidden ---- */
export function NotConnectedBlock({ blockId, title }) {
  return (
    <RelatedBlock blockId={blockId} title={title} isMuted>
      <EmptyLine words={NOT_CONNECTED_WORDS} />
    </RelatedBlock>
  );
}

/* ---- PlainTable({ columns, rows, cellFor }) ----------------------
   In: columns [{ key, label }], rows, cellFor(row, key) -> node.
   Out: Zoho's table in HR's lines; it scrolls inside its own box
   on a phone, never the page.                                   */
export function PlainTable({ columns, rows, cellFor }) {
  return (
    <div className="cb-zr-tablewrap">
      <table className="cb-hr-table cb-zr-table">
        <thead>
          <tr>
            {columns.map((oneColumn) => <th key={oneColumn.key} scope="col">{oneColumn.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((oneRow, rowIndex) => (
            <tr key={oneRow.id || rowIndex}>
              {columns.map((oneColumn) => <td key={oneColumn.key}>{cellFor(oneRow, oneColumn.key)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- AddButton({ label, onAdd }) -- a block's button, for levels that may add ---- */
function AddButton({ label, onAdd }) {
  const accessView = useCustomerAccessView(); // Look up adds nothing (Honest UI)

  if (!accessView.mayWrite) {
    return null;
  }

  return (
    <button type="button" className="bc-btn is-quiet bc-btn-auto cb-hr-btn-sm" onClick={onAdd}>
      <Icon name="plus" /> {label}
    </button>
  );
}

/* ---- useSheetFlow(saveToDoor, onDone) ----------------------------
   The open/closed state of one Add sheet and its save step.
   Out: { isOpen, open, close, save }.                           */
function useSheetFlow(saveToDoor, onDone) {
  const [isOpen, setIsOpen] = useState(false);

  async function save(typedValues) {
    const saveAnswer = await saveToDoor(typedValues);

    if (saveAnswer.ok) {
      setIsOpen(false);
      onDone();
    }

    return saveAnswer;
  }

  return { isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false), save };
}

/*****************************************************************
 *  SECTION 2 - NOTES                                              *
 *  Zoho's Notes block: the add box first, then the notes. The     *
 *  board itself is the same NotesTab (take off with a reason).    *
 *****************************************************************/

/* ---- NotesBlock({ blockId, title, count, customerId, onNotesLoaded }) ----
   onNotesLoaded: handed to the board, which tells it the list each
   time it loads (so the rail's count and this block's count move
   the moment a note is added or taken off).                     */
export function NotesBlock({ blockId, title, count, customerId, onNotesLoaded }) {
  return (
    <RelatedBlock blockId={blockId} title={title} count={count}>
      <NotesTab customerId={customerId} onNotesLoaded={onNotesLoaded} />
    </RelatedBlock>
  );
}

/*****************************************************************
 *  SECTION 3 - PROPERTIES AND REPORTS                             *
 *****************************************************************/

const PROPERTY_COLUMNS = [
  { key: 'name', label: 'Property Name' },
  { key: 'street', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'zip', label: 'Zip code' },
];

/* ---- PropertiesBlock({ blockId, title, customerId, properties, onChanged, addRequest }) ----
   addRequest: a number the title row's "Add a property" raises;
   each new number opens the Add property sheet (Honest UI).     */
export function PropertiesBlock({ blockId, title, customerId, properties, onChanged, addRequest }) {
  const accessView = useCustomerAccessView();
  const sheet = useSheetFlow((typedValues) => addProperty({ ...typedValues, customerId, customFields: {} }), onChanged);
  const openSheet = sheet.open;

  useEffect(() => {
    const wasAskedToAdd = Number(addRequest) > 0;

    if (wasAskedToAdd && accessView.mayWrite) {
      openSheet();
    }
  }, [addRequest, accessView.mayWrite]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- cellFor(property, key) ---- */
  function cellFor(property, key) {
    if (key === 'name') {
      return <Link to={`/properties/${property.id}`}>{property.label || streetWords(property) || 'Unnamed property'}</Link>;
    }
    if (key === 'street') {
      return property.addressLine1 || '—';
    }
    if (key === 'city') {
      return property.city || '—';
    }
    return property.zip || '—';
  }

  return (
    <RelatedBlock
      blockId={blockId}
      title={title}
      count={properties.length}
      actions={<AddButton label="Add property" onAdd={sheet.open} />}
    >
      {properties.length === 0 ? <EmptyLine /> : <PlainTable columns={PROPERTY_COLUMNS} rows={properties} cellFor={cellFor} />}
      {sheet.isOpen ? (
        <SmallFormSheet title="Add property" boxes={NEW_PROPERTY_BOXES} saveLabel="Add property" onSave={sheet.save} onClose={sheet.close} />
      ) : null}
    </RelatedBlock>
  );
}

// Which chip each report state wears (the same words as the Reports tab).
const REPORT_STATUS_CHIPS = {
  sent: { tone: 'ok', icon: 'check', word: 'Sent' },
  'in review': { tone: 'warn', icon: 'clock', word: 'In review' },
  draft: { tone: 'neutral', icon: 'file', word: 'Draft' },
  cancelled: { tone: 'neutral', icon: 'archive', word: 'Cancelled' },
};

const REPORT_COLUMNS = [
  { key: 'address', label: 'Report' },
  { key: 'type', label: 'Type' },
  { key: 'inspected', label: 'Inspected' },
  { key: 'status', label: 'Status' },
];

// The honest words for each way the Report Engine's door can answer.
const REPORT_STATE_WORDS = {
  loading: 'Looking for past reports',
  'not-connected': 'Past reports not connected yet.',
  trouble: 'Past reports could not be reached right now. Try again in a minute.',
  'nothing-to-look-for': 'Add an email or a property address to find past reports.',
};

/* ---- ReportsBlock({ blockId, title, reportAnswer }) --------------
   reportAnswer: null (loading) or fetchPastReports' answer.      */
export function ReportsBlock({ blockId, title, reportAnswer }) {
  const state = reportAnswer ? reportAnswer.state : 'loading';
  const reports = reportAnswer && Array.isArray(reportAnswer.reports) ? reportAnswer.reports : [];

  /* ---- cellFor(report, key) ---- */
  function cellFor(report, key) {
    if (key === 'address') {
      const addressWords = report.address || 'Report';

      return report.url
        ? <a href={report.url} target="_blank" rel="noopener noreferrer">{addressWords}</a>
        : addressWords;
    }
    if (key === 'type') {
      return report.type || '—';
    }
    if (key === 'inspected') {
      return report.inspectionDate ? formatDay(report.inspectionDate) : '—';
    }

    const statusWord = String(report.status || '').toLowerCase();
    const chipLook = REPORT_STATUS_CHIPS[statusWord] || { tone: 'neutral', icon: 'file', word: report.status || 'Unknown' };

    return <StatusChip tone={chipLook.tone} icon={chipLook.icon} word={chipLook.word} />;
  }

  const stateWords = REPORT_STATE_WORDS[state];
  const isNotConnected = state === 'not-connected';

  return (
    <RelatedBlock blockId={blockId} title={title} count={state === 'ok' ? reports.length : undefined} isMuted={isNotConnected}>
      {stateWords ? <EmptyLine words={stateWords} /> : null}
      {state === 'ok' && reports.length === 0 ? <EmptyLine /> : null}
      {state === 'ok' && reports.length > 0 ? <PlainTable columns={REPORT_COLUMNS} rows={reports} cellFor={cellFor} /> : null}
    </RelatedBlock>
  );
}

/*****************************************************************
 *  SECTION 4 - OPEN AND CLOSED ACTIVITIES                         *
 *  Zoho's three columns: Tasks / Meetings / Calls, each with its  *
 *  count. The rows come from recordActivities.js (one rule).      *
 *****************************************************************/

/* ---- describeActivity(row) -- the bold line and the quiet line ---- */
function describeActivity(row) {
  if (row.title) {
    const dueWords = row.dueAt ? `Due ${formatDay(row.dueAt)}` : 'No due date';
    const doneWords = row.completedAt ? `Done ${formatMoment(row.completedAt)}` : '';

    return { name: row.title, meta: doneWords || dueWords };
  }

  if (row.eventName) {
    const eventLook = lookForEvent(row.eventName); // its chip, if it has one

    return {
      name: row.summary,
      meta: [formatMoment(row.occurredAt), row.whoName].filter(Boolean).join(', '),
      chip: eventLook.chip,
    };
  }

  const channelWords = CHANNEL_WORDS[row.channel] || row.channel || 'Activity';

  return { name: row.subject || channelWords, meta: [channelWords, formatMoment(row.occurredAt)].filter(Boolean).join(', ') };
}

/* ---- ActivityColumn({ title, rows }) -- one of the three columns ---- */
function ActivityColumn({ title, rows }) {
  return (
    <div className="cb-zr-col">
      <h3 className="cb-zr-col-title">
        {title}
        <span className="cb-zr-count">{rows.length}</span>
      </h3>
      {rows.length === 0 ? <EmptyLine /> : (
        <ul className="cb-zr-col-rows">
          {rows.map((oneRow, rowIndex) => {
            const words = describeActivity(oneRow);

            return (
              <li key={oneRow.id || rowIndex} className="cb-item">
                <div className="cb-item-name">{words.name}</div>
                {words.chip ? <StatusChip tone={words.chip.tone} icon={words.chip.icon} word={words.chip.word} /> : null}
                {words.meta ? <div className="cb-item-meta">{words.meta}</div> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---- OpenActivitiesBlock({ blockId, title, sorted }) ---- */
export function OpenActivitiesBlock({ blockId, title, sorted }) {
  if (!sorted) {
    return <RelatedBlock blockId={blockId} title={title}><EmptyLine words="Loading" /></RelatedBlock>;
  }

  const openCount = sorted.open.tasks.length + sorted.open.meetings.length + sorted.open.calls.length;

  return (
    <RelatedBlock blockId={blockId} title={title} count={openCount}>
      <div className="cb-zr-cols">
        <ActivityColumn title="Open Tasks" rows={sorted.open.tasks} />
        <ActivityColumn title="Open Meetings" rows={sorted.open.meetings} />
        <ActivityColumn title="Open Calls" rows={sorted.open.calls} />
      </div>
    </RelatedBlock>
  );
}

/* ---- ClosedActivitiesBlock({ blockId, title, sorted, customerId, onLogged }) ----
   "Log activity" (Office and up, customers only) uses the same
   sheet and door as the Timeline.                              */
export function ClosedActivitiesBlock({ blockId, title, sorted, customerId, onLogged }) {
  const sheet = useSheetFlow((typedValues) => logActivity(customerId, typedValues), onLogged);

  if (!sorted) {
    return <RelatedBlock blockId={blockId} title={title}><EmptyLine words="Loading" /></RelatedBlock>;
  }

  const closedCount = sorted.closed.tasks.length + sorted.closed.meetings.length + sorted.closed.calls.length;
  const canLog = Boolean(customerId);

  return (
    <RelatedBlock
      blockId={blockId}
      title={title}
      count={closedCount}
      actions={canLog ? <AddButton label="Log activity" onAdd={sheet.open} /> : null}
    >
      <div className="cb-zr-cols">
        <ActivityColumn title="Closed Tasks" rows={sorted.closed.tasks} />
        <ActivityColumn title="Closed Meetings" rows={sorted.closed.meetings} />
        <ActivityColumn title="Closed Calls" rows={sorted.closed.calls} />
      </div>
      {sorted.otherLoggedCount > 0 ? (
        <p className="cb-zr-empty">{sorted.otherLoggedCount} logged emails, texts or notes are on the Timeline.</p>
      ) : null}
      {sheet.isOpen ? (
        <SmallFormSheet
          title="Log activity"
          boxes={ACTIVITY_BOXES}
          startValues={{ channel: 'call', direction: 'outbound' }}
          saveLabel="Log it"
          onSave={sheet.save}
          onClose={sheet.close}
        />
      ) : null}
    </RelatedBlock>
  );
}

/*****************************************************************
 *  SECTION 5 - CONTACTS, PEOPLE, CONSENT                          *
 *****************************************************************/

const CONTACT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

/* ---- ContactsBlock({ blockId, title, customerId, contacts, onChanged }) ---- */
export function ContactsBlock({ blockId, title, customerId, contacts, onChanged }) {
  const sheet = useSheetFlow((typedValues) => addContact({ ...typedValues, customerId }), onChanged);

  /* ---- cellFor(contact, key) ---- */
  function cellFor(contact, key) {
    if (key === 'name') {
      const nameWords = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'No name';

      return contact.isPrimary ? <>{nameWords} <StatusChip tone="brand" icon="check" word="Main contact" /></> : nameWords;
    }

    return contact[key] || '—';
  }

  return (
    <RelatedBlock blockId={blockId} title={title} count={contacts.length} actions={<AddButton label="Add contact" onAdd={sheet.open} />}>
      {contacts.length === 0 ? <EmptyLine /> : <PlainTable columns={CONTACT_COLUMNS} rows={contacts} cellFor={cellFor} />}
      {sheet.isOpen ? (
        <SmallFormSheet title="Add contact" boxes={NEW_CONTACT_BOXES} saveLabel="Add contact" onSave={sheet.save} onClose={sheet.close} />
      ) : null}
    </RelatedBlock>
  );
}

const PEOPLE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'On this property' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

/* ---- peopleOnProperty(owner, linkedPeople) -- owner first, then the rest ---- */
export function peopleOnProperty(owner, linkedPeople) {
  const everyone = owner ? [{ ...owner, roleWords: 'Owner' }] : [];

  for (const onePerson of linkedPeople || []) {
    const isTheOwner = owner && onePerson.id === owner.id;

    if (!isTheOwner) {
      everyone.push({ ...onePerson, roleWords: 'Linked' });
    }
  }

  return everyone;
}

/* ---- PeopleBlock({ blockId, title, owner, linkedPeople }) ---- */
export function PeopleBlock({ blockId, title, owner, linkedPeople }) {
  const everyone = peopleOnProperty(owner, linkedPeople);

  /* ---- cellFor(person, key) ---- */
  function cellFor(person, key) {
    if (key === 'name') {
      return <Link to={`/customers/${person.id}`}>{person.displayName || 'Unnamed customer'}</Link>;
    }
    if (key === 'role') {
      return person.roleWords;
    }
    if (key === 'phone') {
      return person.primaryPhone || '—';
    }
    return person.primaryEmail || '—';
  }

  return (
    <RelatedBlock blockId={blockId} title={title} count={everyone.length}>
      {everyone.length === 0 ? <EmptyLine /> : <PlainTable columns={PEOPLE_COLUMNS} rows={everyone} cellFor={cellFor} />}
    </RelatedBlock>
  );
}

const CONSENT_COLUMNS = [
  { key: 'type', label: 'Consent to' },
  { key: 'answer', label: 'Answer' },
  { key: 'when', label: 'When' },
  { key: 'how', label: 'How they said it' },
];

/* ---- ConsentBlock({ blockId, title, customerId, records, onChanged }) ----
   records: the consent door's answer (null while loading).       */
export function ConsentBlock({ blockId, title, customerId, records, onChanged }) {
  const sheet = useSheetFlow((typedValues) => recordConsent(customerId, typedValues), onChanged);
  const shownRecords = Array.isArray(records) ? records : [];

  /* ---- cellFor(record, key) ---- */
  function cellFor(record, key) {
    if (key === 'type') {
      return CONSENT_WORDS[record.consentType] || record.consentType;
    }
    if (key === 'answer') {
      const saidYes = record.status === 'granted';

      return <StatusChip tone={saidYes ? 'ok' : 'neutral'} icon={saidYes ? 'check' : 'close'} word={saidYes ? 'Said yes' : 'Took it back'} />;
    }
    if (key === 'when') {
      return formatMoment(record.occurredAt) || '—';
    }
    return record.basis || '—';
  }

  return (
    <RelatedBlock
      blockId={blockId}
      title={title}
      count={records === null ? undefined : shownRecords.length}
      actions={<AddButton label="Record consent" onAdd={sheet.open} />}
    >
      {records === null ? <EmptyLine words="Loading" /> : null}
      {records !== null && shownRecords.length === 0 ? <EmptyLine /> : null}
      {shownRecords.length > 0 ? <PlainTable columns={CONSENT_COLUMNS} rows={shownRecords} cellFor={cellFor} /> : null}
      {sheet.isOpen ? (
        <SmallFormSheet
          title="Record consent"
          boxes={CONSENT_BOXES}
          startValues={{ consentType: 'marketing_email', status: 'granted' }}
          saveLabel="Record it"
          onSave={sheet.save}
          onClose={sheet.close}
        />
      ) : null}
    </RelatedBlock>
  );
}

/*****************************************************************
 *  SECTION 6 - THE OTHER ZOHO LISTS, AND THE TIMELINE HISTORY     *
 *****************************************************************/

/* ---- OtherZohoListsBlock({ labels }) -----------------------------
   WHY (2026-10-15, Fable for James: "restricted to what we have"):
   the Zoho lists Butler does not hold, and the office does not use
   daily, are named together in ONE muted block instead of a page of
   empty boxes. The rail still lists each one, in Zoho's order.    */
export function OtherZohoListsBlock({ labels }) {
  if (!labels || labels.length === 0) {
    return null;
  }

  return (
    <RelatedBlock blockId={OTHER_LISTS_BLOCK_ID} title="More lists in Zoho" isMuted>
      <EmptyLine words={`Not connected yet. Zoho keeps these for now: ${labels.join(', ')}.`} />
    </RelatedBlock>
  );
}

/* ---- timeOfDay(whenNumber) -- "08:06" on this device ---- */
function timeOfDay(whenNumber) {
  const moment = new Date(whenNumber);

  return `${String(moment.getHours()).padStart(2, '0')}:${String(moment.getMinutes()).padStart(2, '0')}`;
}

/* ---- HistoryTimeline({ days, customerId, onLogged }) -------------
   Zoho's Timeline > History: a date pill per day, then each row's
   time, icon, words and who. "Log activity" (Office and up, for a
   customer) uses the same sheet and door as before.             */
export function HistoryTimeline({ days, customerId, onLogged }) {
  const sheet = useSheetFlow((typedValues) => logActivity(customerId, typedValues), onLogged);

  return (
    <div className="cb-zr-history">
      <div className="cb-zr-block-head">
        <h2 className="cb-zr-block-title">Timeline History</h2>
        {customerId ? <div className="cb-zr-block-acts"><AddButton label="Log activity" onAdd={sheet.open} /></div> : null}
      </div>
      {days === null ? <EmptyLine words="Loading" /> : null}
      {days !== null && days.length === 0 ? <EmptyLine words="Nothing has happened on this record yet." /> : null}
      {(days || []).map((oneDay) => (
        <div key={oneDay.dayKey} className="cb-zr-day">
          <span className="cb-zr-day-pill">{formatDay(`${oneDay.dayKey}T12:00:00`)}</span>
          <ul className="cb-zr-day-rows">
            {oneDay.rows.map((oneRow) => (
              <li key={oneRow.id} className="cb-zr-day-row">
                <span className="cb-zr-day-time">{timeOfDay(oneRow.when)}</span>
                <span className="cb-zr-day-icon"><Icon name={oneRow.icon} /></span>
                <span className="cb-zr-day-words">
                  {oneRow.linkUrl ? <a href={oneRow.linkUrl}>{oneRow.words}</a> : oneRow.words}
                  {oneRow.chip ? <StatusChip tone={oneRow.chip.tone} icon={oneRow.chip.icon} word={oneRow.chip.word} /> : null}
                  {oneRow.byWhom ? <span className="cb-zr-day-who">by {oneRow.byWhom}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {sheet.isOpen ? (
        <SmallFormSheet
          title="Log activity"
          boxes={ACTIVITY_BOXES}
          startValues={{ channel: 'call', direction: 'outbound' }}
          saveLabel="Log it"
          onSave={sheet.save}
          onClose={sheet.close}
        />
      ) : null}
    </div>
  );
}
